/**
 * @class  EventEmitter
 * Manage event listeners and callbacks, and trigger events.
 *
 */
export default class EventEmitter {
    constructor(options = null) {
        if (options && options.listeners) {
            this.listeners = options.listeners;
        }
    }

    /**
     * Internal object for keeping track of events and their callbacks
     * @private
     * @type {Object}
     */
    listeners = {};

    /**
     * Attach a callback to an event
     *
     * @param  {String} event
     * @param  {Function} callback
     * @return {void}
     */
    on(event, callback) {
        const currentListeners = this.listeners[event] || [];
        this.listeners[event] = [...currentListeners, callback];
    }

    /**
     * Remove event listeners. If `callback` is not specified, remove all listeners for the given `event`.
     * Pass `callback` to remove only that specific callback function from the event.
     *
     * @param  {String} event
     * @param  {Function} callback
     * @return {void}
     */
    off(event, callback = null) {
        if (callback === null) {
            delete this.listeners[event];
            return;
        }

        const callbacks = this.listeners[event];
        if (callbacks) {
            for (let i = 0; i < callbacks.length; i++) {
                const cb = callbacks[i];
                if (cb === callback) {
                    callbacks.splice(i, 1);
                    break;
                }
            }

            // cleanup event listener items with no callbacks
            if (callbacks.length === 0) {
                delete this.listeners[event];
            }
        }
    }

    /**
     * Remove all event listeners
     *
     * @return {void}
     */
    purge() {
        this.listeners = {};
    }

    /**
     * Trigger an event
     *
     * @param  {String} event
     * @param  {Object[]} params
     * @return {null}
     */
    trigger(event, params) {
        const callbacks = this.listeners[event];
        if (callbacks) {
            callbacks.forEach((callback) => callback.apply(this, params));
        }
    }
}
